// Library imports
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { PageContent, PageHeader } from 'components/common/layout_element/LayoutElement';
import { Row, Stack, CommonModal, LoadingBox } from "_core/components/common/UI";
import * as Constants from "_core/configs/Constants";
import * as LocalStorageUtils from "_core/utils/localstorage";
import { setLoading, setErrorModal } from "_core/slices/scene/SceneSlice";
import AdminOpsPromptListApi from "apis/AdminOpsPromptListApi";
import AdminOpsPromptGetApi from "apis/AdminOpsPromptGetApi";
import AdminOpsPromptSaveApi from "apis/AdminOpsPromptSaveApi";
import AdminOpsPromptApplyApi from "apis/AdminOpsPromptApplyApi";
import AdminOpsPromptStarApi from "apis/AdminOpsPromptStarApi";
import AdminOpsPromptMessageApi from "apis/AdminOpsPromptMessageApi";
import { useOperatorPage } from "./common/useOperatorPage";
import { useDebouncedValue } from "./common/useDebouncedValue";
import { PROMPT_GROUPS, DRAFT_VERSION, verLabel, nowSlash, withStar, withMessage } from "./utils/promptVersions";
import { normalizeNewlines } from "./utils/promptDiff";
import { discardPane } from "./utils/promptPanes";
import { lintPrompt, markupChecksOf } from "./utils/promptLint";
import { isSaveShortcut } from "./utils/shortcut";
import PromptVersionPanel from "./prompt/PromptVersionPanel";
import PromptEditor from "./prompt/PromptEditor";
import PromptDiffModal from "./prompt/PromptDiffModal";
import PromptSaveModal from "./prompt/PromptSaveModal";
import PromptHelpModal from "./prompt/PromptHelpModal";

import './OpsCommon.scss';
import './AdminOpsPrompt.scss';

const EMPTY_MODAL = { show: false, title: "", text: "", onclick: null, oncancel: null, confirmvalue: null, cancelvalue: null };

// 下書きの保存を待つ時間(ms)
const DRAFT_SAVE_DELAY = 500;
// 検査・差分の色帯を更新するまでの待ち時間(ms)
const LINT_DELAY = 150;

const pairOf = (group) => ({ sys: normalizeNewlines(group.system_prompt), user: normalizeNewlines(group.user_prompt) });
const samePair = (a, b) => !!a && !!b && a.sys === b.sys && a.user === b.user;

const readDrafts = (key) => {
    try {
        return JSON.parse(LocalStorageUtils.GetItem(key) || "{}") || {};
    } catch (e) {
        return {};
    }
};

const AdminOpsPrompt = () => {
    const dispatch = useDispatch();
    const { tUser } = useSelector((store) => store.transaction);
    const ExecList = AdminOpsPromptListApi();
    const ExecGet = AdminOpsPromptGetApi();
    const ExecSave = AdminOpsPromptSaveApi();
    const ExecApply = AdminOpsPromptApplyApi();
    const ExecStar = AdminOpsPromptStarApi();
    const ExecMessage = AdminOpsPromptMessageApi();

    // サーバーの状態（グループごと）
    const [groups, setGroups] = useState({});
    // 編集中の本文と、その編集元の版（本文 ≠ 編集元の版 のときが「未保存」）
    const [body, setBody] = useState({});
    const [base, setBase] = useState({});
    const [draftDate, setDraftDate] = useState({});
    const [loaded, setLoaded] = useState(false);

    const [groupKey, setGroupKey] = useState(PROMPT_GROUPS[0].key);
    const [promptTab, setPromptTab] = useState("sys");
    const [verCollapsed, setVerCollapsed] = useState(LocalStorageUtils.GetItem(Constants.LOCALSTORAGE_KEYS.opsPromptVerCollapsed) === "1");

    const [diffModal, setDiffModal] = useState(null);
    const [saveModal, setSaveModal] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [modalInfo, setModalInfo] = useState(EMPTY_MODAL);
    const [isBusy, setIsBusy] = useState(false);

    const bodyCache = useRef(new Map());
    // スターの付け外し：版ごとに「画面の状態」と「サーバーに反映済みの状態」を持ち、順番に送る
    const starSync = useRef({});
    const draftKey = Constants.LOCALSTORAGE_KEYS.opsPromptDraft + "_" + (tUser?.id ?? "");

    // ------------------------------------------------------------------
    // 読み込み
    // ------------------------------------------------------------------
    const showError = (response) => {
        dispatch(setErrorModal({ show: true, text: response?.error_detail ?? { message: Constants.UNKNOWN_ERROR_DETAIL } }));
    };

    const fetchVersion = useCallback(async (gk, v) => {
        const cacheKey = gk + ":" + v;
        if (bodyCache.current.has(cacheKey)) return bodyCache.current.get(cacheKey);
        const { response } = await ExecGet(gk, v);
        if (!response || response.error_code !== 0) {
            showError(response);
            return null;
        }
        const pair = pairOf(response.version);
        bodyCache.current.set(cacheKey, pair);
        return pair;
    }, []);

    const LoadAll = async () => {
        const { response } = await ExecList();
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        const nextGroups = {};
        const nextBody = {};
        const nextBase = {};
        const nextDate = {};
        const drafts = readDrafts(draftKey);

        for (const group of response.group_list) {
            const gk = group.group_key;
            const remote = pairOf(group);
            nextGroups[gk] = group;
            if (group.deployed_v) bodyCache.current.set(gk + ":" + group.deployed_v, remote);

            let baseEntry = { v: group.deployed_v, ...remote };
            let edit = remote;
            const draft = drafts[gk];
            if (draft) {
                const draftPair = { sys: normalizeNewlines(draft.sys), user: normalizeNewlines(draft.user) };
                const baseV = draft.baseVer && group.versions.some((v) => v.v === draft.baseVer) ? draft.baseVer : group.deployed_v;
                if (baseV && baseV !== group.deployed_v) {
                    const pair = await fetchVersion(gk, baseV);
                    if (pair) baseEntry = { v: baseV, sys: pair.sys, user: pair.user };
                }
                if (!samePair(draftPair, baseEntry)) {
                    edit = draftPair;
                    nextDate[gk] = draft.date;
                }
            }
            nextBody[gk] = edit;
            nextBase[gk] = baseEntry;
        }
        setGroups(nextGroups);
        setBody(nextBody);
        setBase(nextBase);
        setDraftDate(nextDate);
        setLoaded(true);
    };

    const { canUse } = useOperatorPage(LoadAll);

    // ------------------------------------------------------------------
    // 状態の判定
    // ------------------------------------------------------------------
    const isUnsaved = useCallback((gk) => loaded && !!body[gk] && !samePair(body[gk], base[gk]), [loaded, body, base]);
    const remoteOf = (gk) => (groups[gk] ? pairOf(groups[gk]) : { sys: "", user: "" });

    // 下書きを端末に残す（テナント単位のキー）
    useEffect(() => {
        if (!loaded) return undefined;
        const timer = setTimeout(() => {
            const drafts = {};
            PROMPT_GROUPS.forEach(({ key }) => {
                if (isUnsaved(key)) drafts[key] = { ...body[key], baseVer: base[key]?.v ?? null, date: draftDate[key] || nowSlash() };
            });
            try {
                if (Object.keys(drafts).length) LocalStorageUtils.SetItem(draftKey, JSON.stringify(drafts));
                else localStorage.removeItem(LocalStorageUtils.GetStorageKey(draftKey));
            } catch (e) {
                // 容量超過などで保存できなくても編集は続けられるようにする
            }
        }, DRAFT_SAVE_DELAY);
        return () => clearTimeout(timer);
    }, [loaded, body, base, draftDate]);

    // 検査（入力が落ち着いてから。保存・適用の可否は現在グループの sys / user 両方で判定する）
    const debouncedBody = useDebouncedValue(body, LINT_DELAY);
    const lintResults = useMemo(() => {
        const group = groups[groupKey];
        const pair = debouncedBody[groupKey];
        if (!group || !pair) return { sys: [], user: [] };
        return {
            sys: lintPrompt(pair.sys, group.rules.sys, markupChecksOf(groupKey, "sys")),
            user: lintPrompt(pair.user, group.rules.user, markupChecksOf(groupKey, "user")),
        };
    }, [debouncedBody, groups, groupKey]);
    const errCount = lintResults.sys.concat(lintResults.user).filter((it) => it.sev === "error").length;

    // ------------------------------------------------------------------
    // 編集
    // ------------------------------------------------------------------
    const onChangeText = useCallback((tab, text) => {
        setBody((prev) => ({ ...prev, [groupKey]: { ...prev[groupKey], [tab]: normalizeNewlines(text) } }));
        setDraftDate((prev) => ({ ...prev, [groupKey]: nowSlash() }));
    }, [groupKey]);

    const selectGroup = (gk) => {
        setGroupKey(gk);
        setPromptTab("sys");
    };

    const toggleVerCollapsed = () => {
        const next = !verCollapsed;
        setVerCollapsed(next);
        LocalStorageUtils.SetItem(Constants.LOCALSTORAGE_KEYS.opsPromptVerCollapsed, next ? "1" : "0");
    };

    const resetEditor = (gk, group) => {
        const remote = pairOf(group);
        setBody((prev) => ({ ...prev, [gk]: remote }));
        setBase((prev) => ({ ...prev, [gk]: { v: group.deployed_v, ...remote } }));
        setDraftDate((prev) => ({ ...prev, [gk]: null }));
    };

    const confirm = (title, text, confirmvalue, onclick) => {
        setModalInfo({ show: true, title, text, onclick, oncancel: null, confirmvalue, cancelvalue: "キャンセル" });
    };

    // 片方のペインだけ適用中の内容に戻す。もう片方も適用中と同じになったら編集元も適用中の版にそろえる
    const discardPaneEdits = (tab) => {
        const gk = groupKey;
        const group = groups[gk];
        const name = tab === "sys" ? "システムプロンプト" : "ユーザープロンプト";
        confirm("編集破棄", `${name}の編集中の内容を破棄して、適用中の内容（${verLabel(group.deployed_v, gk)}）に戻しますか？\nもう片方のプロンプトの編集はそのまま残ります。`, "破棄する", () => {
            const remote = pairOf(group);
            const { pair, clean } = discardPane(body[gk], remote, tab);
            setBody((prev) => ({ ...prev, [gk]: pair }));
            if (clean) {
                setBase((prev) => ({ ...prev, [gk]: { v: group.deployed_v, ...remote } }));
                setDraftDate((prev) => ({ ...prev, [gk]: null }));
            }
        });
    };

    const loadVersion = (v) => {
        const doLoad = async () => {
            dispatch(setLoading(true));
            const pair = await fetchVersion(groupKey, v);
            dispatch(setLoading(false));
            if (!pair) return;
            const next = { sys: pair.sys, user: pair.user };
            setBody((prev) => ({ ...prev, [groupKey]: next }));
            setBase((prev) => ({ ...prev, [groupKey]: { v, ...next } }));
            setDraftDate((prev) => ({ ...prev, [groupKey]: null }));
        };
        if (isUnsaved(groupKey)) {
            confirm("バージョンの読み込み", `編集中の内容は破棄されます。${verLabel(v, groupKey)} を読み込みますか？`, "読み込む", doLoad);
        } else {
            doLoad();
        }
    };

    // ------------------------------------------------------------------
    // 保存・適用
    // ------------------------------------------------------------------
    const updateGroup = (group) => {
        setGroups((prev) => ({ ...prev, [group.group_key]: group }));
        if (group.deployed_v) bodyCache.current.set(group.group_key + ":" + group.deployed_v, pairOf(group));
    };

    const save = async (message, isApply) => {
        const gk = saveModal.groupKey;
        const pair = body[gk];
        setIsBusy(true);
        dispatch(setLoading(true));
        const { response } = await ExecSave({
            groupKey: gk, systemPrompt: pair.sys, userPrompt: pair.user,
            parentV: base[gk]?.v || 0, message, isApply,
        });
        dispatch(setLoading(false));
        setIsBusy(false);
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        const group = response.group;
        const saved = { sys: pair.sys, user: pair.user };
        bodyCache.current.set(gk + ":" + response.saved_v, saved);
        updateGroup(group);
        if (response.is_applied) {
            resetEditor(gk, group);
        } else {
            setBase((prev) => ({ ...prev, [gk]: { v: response.saved_v, ...saved } }));
            setDraftDate((prev) => ({ ...prev, [gk]: null }));
        }
        setSaveModal(null);
    };

    const apply = async (v) => {
        const gk = diffModal.groupKey;
        setIsBusy(true);
        dispatch(setLoading(true));
        const { response } = await ExecApply(gk, v);
        dispatch(setLoading(false));
        setIsBusy(false);
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        const wasUnsaved = isUnsaved(gk);
        updateGroup(response.group);
        // 編集中でなければ、エディタも適用した内容にそろえる
        if (!wasUnsaved) resetEditor(gk, response.group);
        setDiffModal(null);
    };

    // 最後に押された状態になるまでサーバーへ送る。失敗したら反映済みの状態に戻す
    const flushStar = async (gk, v, state) => {
        state.busy = true;
        while (state.desired !== state.confirmed) {
            const { response } = await ExecStar(gk, v, state.desired);
            if (!response || response.error_code !== 0) {
                state.desired = state.confirmed;
                setGroups((prev) => ({ ...prev, [gk]: withStar(prev[gk], v, state.confirmed) }));
                showError(response);
                break;
            }
            state.confirmed = response.is_starred;
        }
        state.busy = false;
    };

    // スターは押した時点で画面に反映し、API の応答は待たない（本文や適用状態には影響しない）
    const toggleStar = (v, isStarred) => {
        const gk = groupKey;
        const key = gk + ":" + v;
        setGroups((prev) => ({ ...prev, [gk]: withStar(prev[gk], v, isStarred) }));
        const state = starSync.current[key] || (starSync.current[key] = { confirmed: !isStarred, busy: false });
        state.desired = isStarred;
        if (!state.busy) flushStar(gk, v, state);
    };

    // 変更メモの書き換え（応答を待ってから画面に反映する）。成功したかを返す
    const saveMessage = async (v, message) => {
        const gk = groupKey;
        const { response } = await ExecMessage(gk, v, message);
        if (!response || response.error_code !== 0) {
            showError(response);
            return false;
        }
        setGroups((prev) => ({ ...prev, [gk]: withMessage(prev[gk], response.v, response.message) }));
        return true;
    };

    const openSave = (isApply) => {
        if (errCount) return;
        setSaveModal({ groupKey, isApply });
    };

    // Ctrl+S（Cmd+S）で保存ボタンと同じくメモ入力を開く。ブラウザの「ページを保存」は常に止める
    const shortcutRef = useRef(null);
    shortcutRef.current = () => {
        const isModalOpen = !!saveModal || !!diffModal || helpOpen || modalInfo.show;
        if (!loaded || isModalOpen || isBusy || !isUnsaved(groupKey) || errCount) return;
        openSave(false);
    };
    useEffect(() => {
        const onKeyDown = (e) => {
            if (!isSaveShortcut(e)) return;
            e.preventDefault();
            shortcutRef.current();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const openDiff = async (v, mode) => {
        if (v === null) {
            setDiffModal({ groupKey, mode, v: null, right: body[groupKey], tab: promptTab });
            return;
        }
        dispatch(setLoading(true));
        const pair = await fetchVersion(groupKey, v);
        dispatch(setLoading(false));
        if (pair) setDiffModal({ groupKey, mode, v, right: pair, tab: promptTab });
    };

    // ------------------------------------------------------------------
    // 描画
    // ------------------------------------------------------------------
    const group = groups[groupKey];
    const currentBody = body[groupKey] || { sys: "", user: "" };
    const remote = remoteOf(groupKey);

    return (
        <PageContent pageId="AdminOpsPrompt">
            <div className="ops-scope width-full">
                <PageHeader>
                    <Row className="flex-both align-center width-full">
                        <Stack className="spacing-4px">
                            <div className="ops-title-row">
                                <div className="page-header-title">プロンプト管理</div>
                                <button type="button" className="ops-help-button" title="使い方を表示します" onClick={() => setHelpOpen(true)}>
                                    <i className="bi bi-question-circle" />
                                </button>
                            </div>
                            <div className="page-header-detail">応答生成に使うプロンプトを編集・バージョン管理します。</div>
                        </Stack>
                    </Row>
                </PageHeader>

                <div className="ops-prompt-body">
                    {!loaded || !group ?
                        <div className="ops-prompt-loading">{canUse && <LoadingBox />}</div>
                        :
                        <>
                            <PromptVersionPanel
                                groupKey={groupKey}
                                groups={groups}
                                collapsed={verCollapsed}
                                onToggleCollapsed={toggleVerCollapsed}
                                isUnsaved={isUnsaved}
                                draft={isUnsaved(groupKey) ? { baseVer: base[groupKey]?.v ?? null, date: draftDate[groupKey] || nowSlash() } : null}
                                errCount={errCount}
                                currentV={isUnsaved(groupKey) ? DRAFT_VERSION : (base[groupKey]?.v ?? null)}
                                onSelectGroup={selectGroup}
                                onSave={() => openSave(false)}
                                onLoad={loadVersion}
                                onDiff={(v) => openDiff(v, "diff")}
                                onApply={(v) => (v === null ? openSave(true) : openDiff(v, "apply"))}
                                onToggleStar={toggleStar}
                                onSaveMessage={saveMessage}
                            />
                            <PromptEditor
                                groupKey={groupKey}
                                groups={groups}
                                isUnsaved={isUnsaved}
                                focusTab={promptTab}
                                texts={currentBody}
                                remotes={remote}
                                issues={lintResults}
                                onChangeText={onChangeText}
                                onFocusTab={setPromptTab}
                                onSelectGroup={selectGroup}
                                onDiscardPane={discardPaneEdits}
                            />
                        </>
                    }
                </div>

                {saveModal &&
                    <PromptSaveModal
                        modal={saveModal}
                        group={groups[saveModal.groupKey]}
                        baseVer={base[saveModal.groupKey]?.v ?? null}
                        basePair={base[saveModal.groupKey]}
                        current={body[saveModal.groupKey]}
                        initialTab={promptTab}
                        isBusy={isBusy}
                        onClose={() => setSaveModal(null)}
                        onSave={save}
                    />
                }
                {helpOpen && <PromptHelpModal groups={groups} groupKey={groupKey} onClose={() => setHelpOpen(false)} />}
                {diffModal &&
                    <PromptDiffModal
                        modal={diffModal}
                        group={groups[diffModal.groupKey]}
                        isBusy={isBusy}
                        onChangeTab={(tab) => setDiffModal((prev) => ({ ...prev, tab }))}
                        onClose={() => setDiffModal(null)}
                        onApply={() => apply(diffModal.v)}
                    />
                }
                <CommonModal modalInfo={modalInfo} setModalInfo={setModalInfo} />
            </div>
        </PageContent>
    );
};

export default AdminOpsPrompt;
